import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Printer, BookOpen, ChevronDown, ArrowUp, MessagesSquare } from 'lucide-react';
import { HELP_SECTIONS } from './sections';
import { HELP_CHANGELOG } from './helpChangelog';
import { HelpSearchBox, HelpSearchResults } from './HelpSearchPanel';
import { useHelpSearch } from './useHelpSearch';
import { isTauri } from '../../utils/tauri';

interface HelpPageProps {
  /** 전달하면 오버레이 모드(닫기 버튼 + ESC). 없으면 /help 단독 페이지 모드 */
  onClose?: () => void;
  /** 열자마자 보여 줄 장(`HELP_SECTIONS`의 id) — 홈 '알고 계셨나요?' 팁의 '사용설명서에서 보기'용 */
  initialSectionId?: string;
}

/**
 * 본문 전체. 검색어를 칠 때마다 HelpPage가 다시 렌더되므로 memo로 12개 장의 재렌더를 막는다.
 * 검색(useHelpSearch)은 이 DOM을 직접 읽는다 — 장 Body는 fragment로 반환해야 소제목(h3)이
 * `<section>`의 직계 자식이 되어 소제목 단위 결과로 나뉜다.
 */
const HelpBody = React.memo(function HelpBody() {
  return (
  <>
    {HELP_SECTIONS.map(({ id, title, summary, icon: Icon, Body }, i) => (
      <section key={id} id={`help-${id}`} className="scroll-mt-6 mb-14 last:mb-6 break-before-auto">
        <div className="flex items-start gap-3 pb-3 mb-1 border-b-2 border-slate-800">
          <div className="h-10 w-10 shrink-0 bg-slate-800 rounded-lg flex items-center justify-center">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900 leading-tight">
              <span className="text-slate-400 mr-1.5">{i + 1}.</span>
              {title}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{summary}</p>
          </div>
        </div>
        <Body />
      </section>
    ))}
  </>
  );
});

type MobilePanel = 'none' | 'toc' | 'results';

export const HelpPage: React.FC<HelpPageProps> = ({ onClose, initialSectionId }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const [activeId, setActiveId] = useState<string>(HELP_SECTIONS[0].id);
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>('none');
  const [showTop, setShowTop] = useState(false);
  const [query, setQuery] = useState('');
  const queryRef = useRef(query);
  queryRef.current = query;

  const search = useHelpSearch(contentRef, scrollRef, query);
  const hasQuery = query.trim().length > 0;

  const changeQuery = useCallback((value: string) => {
    setQuery(value);
    setMobilePanel(value.trim() ? 'results' : 'none');
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setMobilePanel((p) => (p === 'results' ? 'none' : p));
  }, []);

  /** 지금 보이는 검색창(데스크톱 목차 위 / 모바일 막대)에 포커스 */
  const focusSearch = useCallback(() => {
    const el = [desktopInputRef.current, mobileInputRef.current].find((x) => x && x.offsetParent !== null);
    if (!el) return;
    el.focus();
    el.select();
  }, []);

  // 단축키. capture 단계에서 처리해 도움말이 열려 있는 동안 뒤쪽 채팅 화면의 전역 단축키
  // (Ctrl+F 대화 검색, ESC 검색 닫기)가 함께 동작하는 것을 막는다.
  // Ctrl+F는 브라우저 찾기 대신 설명서 검색으로(PC앱 WebView2·모바일엔 브라우저 찾기가 없다).
  // ESC는 검색어가 있으면 지우기가 먼저, 그다음 닫기.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        e.stopPropagation();
        focusSearch();
        return;
      }
      if (e.key === 'Escape') {
        if (queryRef.current) {
          e.stopPropagation();
          clearSearch();
        } else if (onClose) {
          e.stopPropagation();
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, focusSearch, clearSearch]);

  // 스크롤 위치에 따라 목차 항목 강조 + 맨 위로 버튼 표시
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id.replace(/^help-/, ''));
      },
      { root, rootMargin: '0px 0px -75% 0px', threshold: 0 }
    );

    HELP_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(`help-${id}`);
      if (el) observer.observe(el);
    });

    const onScroll = () => setShowTop(root.scrollTop > 600);
    root.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      root.removeEventListener('scroll', onScroll);
    };
  }, []);

  const goTo = useCallback((id: string) => {
    setActiveId(id);
    setMobilePanel('none');
    document.getElementById(`help-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // 특정 장으로 열기 — 마운트 1회. 부드러운 스크롤은 쓰지 않는다(열리자마자 긴 거리를 굴러가 어지럽다).
  // 다음 프레임에 재는 이유: 본문이 막 붙은 첫 프레임에는 레이아웃이 끝나지 않아 위치가 어긋날 수 있다.
  useEffect(() => {
    if (!initialSectionId || !HELP_SECTIONS.some((s) => s.id === initialSectionId)) return;
    setActiveId(initialSectionId);
    const raf = requestAnimationFrame(() => {
      document.getElementById(`help-${initialSectionId}`)?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, []); // 열릴 때 한 번만 — 열린 채 다른 장을 고르는 경로는 없다

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const { next, prev, goToResult, reveal } = search;

  // 데스크톱: 결과 목록이 옆에 계속 보이므로 Enter는 곧장 다음 결과로
  const onDesktopEnter = useCallback((backward: boolean) => (backward ? prev() : next()), [next, prev]);

  // 모바일: 결과 패널이 본문을 가리고 있어 첫 결과를 아직 못 봤다 — Enter는 패널을 닫고 키보드를 내려
  // 지금 결과를 보여 준다. 그다음부터는 검색창의 위·아래 버튼으로 넘긴다.
  // 이동은 다음 프레임에 — 패널이 닫혀 본문이 커지기 전에 재면 매치가 화면 아래쪽에 놓인다.
  const onMobileEnter = useCallback(() => {
    setMobilePanel('none');
    mobileInputRef.current?.blur();
    requestAnimationFrame(reveal);
  }, [reveal]);

  const selectResult = useCallback(
    (resultIndex: number) => {
      setMobilePanel('none');
      mobileInputRef.current?.blur();
      requestAnimationFrame(() => goToResult(resultIndex));
    },
    [goToResult]
  );

  const activeSection = HELP_SECTIONS.find((s) => s.id === activeId) ?? HELP_SECTIONS[0];

  const toc = (
    <nav className="space-y-0.5">
      {HELP_SECTIONS.map(({ id, title, icon: Icon }, i) => {
        const isActive = id === activeId;
        return (
          <button
            key={id}
            type="button"
            aria-current={isActive ? 'true' : undefined}
            onClick={() => goTo(id)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 min-h-[40px] text-sm rounded-lg text-left transition-colors ${
              isActive
                ? 'bg-brand-50 text-brand-700 font-semibold'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-brand-600' : 'text-slate-400'}`} />
            <span className="truncate">
              <span className="text-xs text-slate-400 mr-1.5">{i + 1}</span>
              {title}
            </span>
          </button>
        );
      })}
    </nav>
  );

  const results = (
    <HelpSearchResults
      terms={search.terms}
      results={search.results}
      truncated={search.truncated}
      pending={search.pending}
      activeIndex={search.currentResult}
      onSelect={selectResult}
      onSuggest={changeQuery}
    />
  );

  const searchBoxProps = {
    value: query,
    onChange: changeQuery,
    onClear: clearSearch,
    onPrev: prev,
    onNext: next,
    current: search.current,
    total: search.total,
    truncated: search.truncated,
    pending: search.pending,
  };

  return (
    <div
      className={
        onClose
          // 앱 안 오버레이는 PC앱 제목 표시줄 아래에서 시작한다(TitleBar의 --titlebar-h 주석)
          ? 'fixed inset-x-0 bottom-0 top-[var(--titlebar-h,0px)] z-50 bg-slate-900/60 flex items-center justify-center md:p-6 print:static print:bg-white print:p-0'
          : 'fixed inset-0 z-50 bg-slate-100 flex items-center justify-center print:static print:bg-white'
      }
      onClick={onClose ? (e) => e.target === e.currentTarget && onClose() : undefined}
      role="dialog"
      aria-modal={onClose ? true : undefined}
      aria-label="INA-TALK 사용 설명서"
    >
      <div className="relative w-full h-full md:max-w-6xl bg-white md:rounded-xl shadow-2xl flex flex-col overflow-hidden print:shadow-none print:h-auto print:max-w-none print:overflow-visible print:rounded-none print:block">
        {/* 상단 바 */}
        <header className="shrink-0 flex items-center gap-3 px-4 md:px-6 py-3 border-b border-slate-200 bg-white print:hidden">
          <div className="h-9 w-9 shrink-0 bg-brand-600 rounded-lg flex items-center justify-center">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-bold text-slate-800 leading-tight">INA-TALK 사용 설명서</h1>
            <p className="text-xs text-slate-500 truncate">
              {/* 좁은 화면에서는 줄이 잘려 개정일이 안 보이므로 환경 문구를 숨긴다 */}
              <span className="hidden sm:inline">
                {isTauri() ? 'PC 앱 기준으로 설명합니다' : '웹 버전으로 보고 있습니다'}
                {' · '}
              </span>
              {/* 설명서 개정일 = 업데이트 내역의 최신 항목. 누르면 15장으로 */}
              <button
                type="button"
                onClick={() => goTo('changelog')}
                title="업데이트 내역 보기"
                className="text-brand-700 hover:underline"
              >
                {HELP_CHANGELOG[0].date} 개정
              </button>
            </p>
          </div>
          <button
            onClick={() => window.print()}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            title="인쇄하기"
          >
            <Printer className="h-4 w-4" />
            인쇄
          </button>
          {onClose ? (
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              title="닫기 (Esc)"
            >
              <X className="h-5 w-5" />
            </button>
          ) : (
            <a
              href="/"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors"
            >
              <MessagesSquare className="h-4 w-4" />
              채팅 열기
            </a>
          )}
        </header>

        {/* 좁은 화면: 검색창 + 목차 버튼, 아래 펼침 패널은 목차 또는 검색 결과.
            shrink를 허용해(min-h-0) 키보드가 떠 화면이 줄면 패널이 먼저 줄어든다 */}
        <div className="md:hidden min-h-0 flex flex-col border-b border-slate-200 print:hidden">
          <div className="shrink-0 flex items-center gap-2 px-3 py-2 bg-slate-50">
            <div className="flex-1 min-w-0">
              <HelpSearchBox
                {...searchBoxProps}
                inputRef={mobileInputRef}
                onEnter={onMobileEnter}
                onFocus={() => {
                  if (queryRef.current.trim()) setMobilePanel('results');
                }}
              />
            </div>
            <button
              type="button"
              aria-expanded={mobilePanel === 'toc'}
              aria-controls="help-mobile-panel"
              aria-label={`목차 (${activeSection.title})`}
              title="목차"
              onClick={() => setMobilePanel((p) => (p === 'toc' ? 'none' : 'toc'))}
              className={`shrink-0 flex items-center gap-1 h-10 px-2.5 rounded-lg border transition-colors ${
                mobilePanel === 'toc'
                  ? 'bg-brand-100 border-brand-200 text-brand-700'
                  : 'bg-white border-slate-200 text-slate-600'
              }`}
            >
              <activeSection.icon className="h-4 w-4 text-brand-600" />
              <ChevronDown
                className={`h-4 w-4 text-slate-400 transition-transform ${mobilePanel === 'toc' ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
          {mobilePanel !== 'none' && (
            <div
              id="help-mobile-panel"
              className={`min-h-0 overflow-y-auto overscroll-contain border-t border-slate-100 p-2 ${
                mobilePanel === 'toc' ? 'max-h-72' : 'max-h-[60vh]'
              }`}
            >
              {mobilePanel === 'toc' ? toc : results}
            </div>
          )}
        </div>

        <div className="flex-1 flex min-h-0 print:block print:h-auto">
          {/* 목차 / 검색 결과 */}
          <aside className="hidden md:flex md:flex-col w-60 shrink-0 border-r border-slate-200 bg-slate-50/60 print:hidden">
            <div className="shrink-0 p-3 pb-2">
              <HelpSearchBox {...searchBoxProps} inputRef={desktopInputRef} onEnter={onDesktopEnter} />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">{hasQuery ? results : toc}</div>
          </aside>

          {/* 본문 */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto print:overflow-visible print:h-auto">
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 md:py-8">
              <div ref={contentRef}>
                <HelpBody />
              </div>

              <footer className="pt-6 mt-8 border-t border-slate-200 text-center space-y-2">
                <p className="text-xs text-slate-400">
                  이 설명서는 앱에 내장되어 있어 언제든 왼쪽 탭 바의 물음표 버튼으로 다시 열 수 있습니다.
                </p>
                {!onClose && (
                  <a href="/" className="inline-block text-xs text-brand-600 hover:text-brand-700">
                    채팅으로 돌아가기
                  </a>
                )}
              </footer>
            </div>
          </div>
        </div>

        {/* 맨 위로 */}
        {showTop && (
          <button
            onClick={scrollToTop}
            className="absolute bottom-5 right-5 p-2.5 bg-slate-800 text-white rounded-full shadow-lg hover:bg-slate-700 transition-colors print:hidden"
            title="맨 위로"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default HelpPage;
